import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PermissionPicker } from '../../../../components/PermissionPicker';
import { Skeleton } from '../../../../components/Skeleton';
import { Stepper, type StepDescriptor } from '../../../../components/Stepper';
import { PasswordFields, usePasswordFieldsState } from '../../../../components/inputs/PasswordFields';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { rolesApi } from '../../roles/rolesApi';
import { usePermissionCatalog } from '../../roles/usePermissionCatalog';
import { usersApi } from '../usersApi';
import type { RoleListItem } from '../../roles/roleTypes';
import type { UserCredentialIssued } from '../userTypes';
import { sampleUser } from '../utils/sampleUser';

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  /** Fired after a successful create so the caller can refresh dependent counts. */
  onCreated: () => void;
}

interface CreateStep extends StepDescriptor {
  content: React.ReactNode;
  valid: boolean;
}

/**
 * Create a user — reference §5, stepped like the edit modal.
 *
 * Step 1 is the identity and the first credential; step 2 is the access — the role, and the
 * subset of its permissions this account should hold. The role sits in step 2 because it is
 * the input to the table directly beneath it, not a fact about the person.
 *
 * The confirm checkbox appears on the last step only: it certifies the whole transaction,
 * and offering it on step 1 invites ticking before the access has even been chosen.
 */
export function CreateUserModal({ open, onClose, onCreated }: CreateUserModalProps) {
  const [step, setStep] = useState(0);
  const [alias, setAlias] = useState('');
  const [email, setEmail] = useState('');
  const { state: passwordState, onChange: onPasswordChange } = usePasswordFieldsState();
  const [roleId, setRoleId] = useState('');
  const [permissionKeys, setPermissionKeys] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);

  const { catalog } = usePermissionCatalog(open);
  const [roles, setRoles] = useState<RoleListItem[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleKeys, setRoleKeys] = useState<string[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [issued, setIssued] = useState<UserCredentialIssued | null>(null);
  const [issuedPassword, setIssuedPassword] = useState('');

  // Reset on every open so a previous attempt never bleeds into a new one.
  useEffect(() => {
    if (!open) return;

    setStep(0);
    setAlias('');
    setEmail('');
    setRoleId('');
    setPermissionKeys([]);
    setRoleKeys([]);
    setConfirmed(false);
    setError(null);
    setPermissionsError(null);
    setIssued(null);
    setIssuedPassword('');
  }, [open]);

  // The role picker and the catalog both belong to another module; the backend's declared
  // cross-module reference read is what makes them readable to a users.* holder.
  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setRolesLoading(true);

    rolesApi
      .list({ status: 'active', pageSize: 100 }, controller.signal)
      .then((result) => setRoles(result.items))
      .catch(() => {
        if (!controller.signal.aborted) setRoles([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setRolesLoading(false);
      });

    return () => controller.abort();
  }, [open]);

  // Selecting a role re-seeds the table: its grants become the ceiling, all enabled. Trimming
  // is a deliberate act on top of that, so a role change deliberately discards earlier ticks.
  useEffect(() => {
    if (!open || !roleId) {
      setRoleKeys([]);
      setPermissionKeys([]);
      return;
    }

    const controller = new AbortController();
    setPermissionsLoading(true);
    setPermissionsError(null);

    rolesApi
      .get(roleId, controller.signal)
      .then((detail) => {
        setRoleKeys(detail.permissionKeys);
        setPermissionKeys(detail.permissionKeys);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRoleKeys([]);
        setPermissionKeys([]);
        setPermissionsError('Could not load the permissions for that role.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setPermissionsLoading(false);
      });

    return () => controller.abort();
  }, [open, roleId]);

  const accountValid =
    alias.trim().length > 0 && /^\S+@\S+\.\S+$/.test(email.trim()) && passwordState.valid;

  // An account may legitimately start with nothing ticked; only a failed load blocks.
  const accessValid = roleId.length > 0 && !permissionsLoading && permissionsError === null;
  const canSave = accountValid && accessValid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const result = await usersApi.create({
        email: email.trim(),
        alias: alias.trim(),
        // Always supplied: the administrator has already seen it on screen, so the server
        // has nothing left to mint and the value cannot differ from what was shown.
        password: passwordState.password,
        roleId,
        permissionKeys,
        mustChangePassword: true,
      });

      setIssuedPassword(passwordState.password);
      setIssued(result);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not create the user.', 0);

      setError(failure);

      /*
        A stepped form owes the reader the STEP as well as the field. Rejected on step 2 with
        a banner naming an alias they cannot see, they would have to walk back through the
        wizard to find it.
      */
      focusValidationError(failure, {
        idPrefix: 'create-user',
        fieldIds: { roleId: 'create-user-roles' },
        stepOf: (field) => (field === 'roleId' || field.startsWith('permission') ? 1 : 0),
        onStep: setStep,
      });
    } finally {
      setSaving(false);
    }
  }

  /** Fills every required field so the primary action enables on the first try. */
  function autogenerate() {
    const sample = sampleUser();
    setAlias(sample.alias);
    setEmail(sample.email);

    // Resolve the role to a live catalog row rather than inventing an id. The permission
    // effect then seeds step 2 from whatever that role actually grants.
    const fallbackRole = roles.find((r) => r.name === 'Staff') ?? roles[0];
    if (fallbackRole) setRoleId(fallbackRole.id);
  }

  const steps: CreateStep[] = [
    {
      key: 'account',
      label: 'Account',
      valid: accountValid,
      content: (
        <div className="flex flex-col gap-4">
          <TextField
            id="create-user-alias"
            label="Alias"
            required
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            error={error?.fieldError('alias')}
            description="Short handle for this account. The full name is added later from Edit."
            disabled={saving}
          />

          <TextField
            id="create-user-email"
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error?.fieldError('email')}
            description="Used as the sign-in name. Must be unique."
            disabled={saving}
          />

          <PasswordFields
            idPrefix="create-user"
            disabled={saving}
            serverError={error?.fieldError('password')}
            onChange={onPasswordChange}
            resetKey={open}
          />
        </div>
      ),
    },
    {
      key: 'permissions',
      label: 'Permissions',
      valid: accessValid,
      content: (
        <div className="flex flex-col gap-4">
          <Select
            id="create-user-roles"
            label="Role"
            required
            remote
            loading={rolesLoading}
            description="One role per user. Its permissions become the starting point below."
            value={roleId || null}
            onChange={setRoleId}
            options={roles.map((role) => ({
              value: role.id,
              label: role.name,
              right: `${role.permissionCount} perms`,
            }))}
            disabled={saving}
          />

          {permissionsLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : permissionsError ? (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700"
            >
              {permissionsError}
            </p>
          ) : !roleId ? (
            <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
              Choose a role to see what it grants.
            </p>
          ) : (
            <PermissionPicker
              idPrefix="create-user-perm"
              catalog={catalog}
              limitToKeys={roleKeys}
              value={permissionKeys}
              onChange={setPermissionKeys}
              disabled={saving}
              description="Everything this role grants, enabled. Turn off anything this account should not hold — the role itself is unchanged."
            />
          )}
        </div>
      ),
    },
  ];

  const isLastStep = step === steps.length - 1;

  if (issued) {
    return (
      <Modal
        open={open}
        onClose={() => {
          setIssued(null);
          onCreated();
        }}
        title="User created"
        size="lg"
        id="user-created-modal"
        footer={
          <Button
            id="user-created-done"
            variant="primary"
            onClick={() => {
              setIssued(null);
              onCreated();
            }}
          >
            Done
          </Button>
        }
      >
        <CredentialPanel issued={issued} password={issued.generatedPassword ?? issuedPassword} />
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create user"
      size="xl"
      id="create-user-modal"
      confirm={
        isLastStep
          ? {
              id: 'create-user-confirm',
              checked: confirmed,
              onChange: setConfirmed,
              disabled: saving,
            }
          : undefined
      }
      /*
        The shell renders this, so its variant, icon, label and leading placement are the
        same here as on every other create form — this button was the pattern the prop was
        lifted from.
      */
      autogenerate={{
        id: 'create-user-autogenerate',
        onGenerate: autogenerate,
        disabled: saving || rolesLoading,
      }}
      footer={
        <>
          <Button id="create-user-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {/* Back pairs with Next and renders from the second step on — see "More than one
              step means a Back button". Never gated on validity: going back is how an invalid
              step gets fixed. */}
          {step > 0 ? (
            <Button
              id="create-user-back"
              variant="secondary"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Button
              id="create-user-next"
              variant="secondary"
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              disabled={!steps[step].valid}
            >
              Next
            </Button>
          ) : null}

          <Button
            id="create-user-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
            {Object.entries(error.fields).map(([field, messages]) => (
              <p key={field} className="mt-1 text-xs text-red-600">
                {messages.join(' ')}
              </p>
            ))}
          </div>
        ) : null}

        <Stepper
          id="create-user"
          steps={steps.map(({ key, label }) => ({ key, label }))}
          activeIndex={step}
          onJump={(index) => {
            // Forward movement requires every earlier step to be valid; back is free.
            if (index <= step || steps.slice(0, index).every((s) => s.valid)) {
              setStep(index);
            }
          }}
        />

        <div
          id={`create-user-panel-${steps[step].key}`}
          role="tabpanel"
          aria-labelledby={`create-user-step-${steps[step].key}`}
        >
          {steps[step].content}
        </div>
      </div>
    </Modal>
  );
}

/** Shows the password exactly once, with an explicit warning that it will not reappear. */
function CredentialPanel({
  issued,
  password,
}: {
  issued: UserCredentialIssued;
  password: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!password) return;

    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the value is visible on screen regardless.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-slate-600">
        The account for <span className="font-mono tabular-nums">{issued.email}</span> has been
        created.
      </p>

      {password ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-800">
            This password is shown once and is not stored anywhere. Copy it now and deliver it
            through a channel you trust.
          </p>

          <div className="mt-2.5 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border border-amber-200 bg-white px-3 py-2 font-mono tabular-nums text-sm text-slate-800">
              {password}
            </code>
            <Button
              id="user-created-copy"
              variant="secondary"
              onClick={() => void copy()}
              leading={
                copied ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )
              }
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      ) : null}

      {issued.mustChangePassword ? (
        <p className="text-xs text-slate-500">
          The user must change this password the first time they sign in.
        </p>
      ) : null}
    </div>
  );
}
